import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Menu, X, ChevronDown, Calculator, BookOpen, PenSquare, User, School, School2, Wallet, Book, Brain, FileSpreadsheet, DollarSign, HelpingHand, HomeIcon } from 'lucide-react';

function Navigation() {
  const { user, logout, hasRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);
  const [showProjectsSubmenu, setShowProjectsSubmenu] = useState(false);
  const getCurrentPageName = () => {
    const path = location.pathname;
    if (path === '/dashboard') return 'Dashboard';
    if (path.startsWith('/portfolio')) return 'Portfolio';
    if (path.startsWith('/calculators')) return 'Calculators';
    if (path.startsWith('/training')) return 'Training';
    if (path.startsWith('/articles')) return 'Articles';
    if (path.startsWith('/re')) return 'Real Estate';
    if (path.startsWith('/hnw')) return 'HNW';
    if (path.startsWith('/prv')) return 'PRV';
    if (path === '/account') return 'Account';
    return '';
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Article categories matching Articles.js
  const articleCategories = [
    { id: 'all', name: 'All Articles', path: '/articles' },
    { id: 'investing', name: 'Investing', path: '/articles?category=investing' },
    { id: 'retirement', name: 'Retirement Planning', path: '/articles?category=retirement' },
    { id: 'taxes', name: 'Tax Strategy', path: '/articles?category=taxes' },
    { id: 'credit', name: 'Credit & Debt', path: '/articles?category=credit' },
    { id: 'estate', name: 'Estate Planning', path: '/articles?category=estate' }
  ];

  const renderArticlesDropdown = () => (
    <div className="relative group">
      <Link to="/articles" className="text-white hover:text-gray-300 px-3 py-2 inline-flex items-center">
        <BookOpen className="w-4 h-4 mr-1" />
        News <ChevronDown className="ml-1 h-4 w-4" />
      </Link>
      <div className="absolute left-0 hidden group-hover:block w-64 py-1 bg-white rounded-md shadow-lg z-50">
        {articleCategories.map(category => (
          <Link
            key={category.id}
            to={category.path}
            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
          >
            {category.name}
          </Link>
        ))}
        <div className="border-t border-gray-100 mt-1 pt-1">
          <Link
            to="/articles/create"
            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
          >
            <PenSquare className="w-4 h-4 mr-2" />
            Create Article
          </Link>
        </div>
      </div>
    </div>
  );


  const renderMobileArticlesMenu = () => (
    <div className="relative">
      <button className="text-white hover:text-gray-300 w-full text-left flex items-center justify-between" 
        onClick={() => setActiveMenu(activeMenu === 'articles' ? null : 'articles')}>
        <span className="flex items-center">
          <BookOpen className="w-4 h-4 mr-2" />
          News
        </span>
        <ChevronDown className="w-4 h-4" />
      </button>
      <div className={`mt-2 ml-6 space-y-2 ${activeMenu === 'articles' ? 'block' : 'hidden'}`}>
        {articleCategories.map(category => (
          <Link
            key={category.id}
            to={category.path}
            className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm"
          >
            {category.name}
          </Link>
        ))}
        <Link
          to="/articles/create"
          className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm flex items-center"
        >
          <PenSquare className="w-4 h-4 mr-2" />
          Create Article
        </Link>
      </div>
    </div>
  );

  return (
    <nav className="bg-gray-800">
      <div className="max-w-7xl mx-auto pl-2 pr-4">
        <div className="flex justify-between h-16">
          {/* Logo and Brand */}
          <div className="flex items-center">
            <Link to="/" className="text-white text-xl font-bold">
              AiFinCoach
            </Link>
          </div>

          {user ? (
            <>
              {/* Desktop Navigation */}
              <div className="hidden md:flex items-center space-x-4">
                <div className="text-gray-300 text-3xs">
                  {/*user.email} : {getCurrentPageName()*/}
                </div>

                {/* Portfolio Menu */}
                <div className="relative group">
                  <Link to="" className="text-white hover:text-gray-300 px-3 py-2 inline-flex items-center">
                    <Wallet className="w-4 h-4 mr-1" />
                    Port <ChevronDown className="ml-1 h-4 w-4" />
                  </Link>
                  <div className="absolute left-0 hidden group-hover:block w-64 py-1 bg-white rounded-md shadow-lg z-50">
                  <Link to="/portfolio/currentholdings" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Current Holdings
                    </Link>
                    <Link to="/portfolio/analysis" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Portfolio Analysis
                    </Link>
                    <Link to="/portfolio/performance" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Portfolio Performance
                    </Link>
                    <Link to="/portfolio/scenarios" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Portfolio Scenarios
                    </Link>
                    <Link to="/portfolio/globalmacrodashboard" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Portfolio Global Macro Dashboard
                    </Link>
                    <Link to="/portfolio/historical-currency-rates" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Historical Currency Rates
                    </Link>
                    
                  </div>
                </div>

                {/* Calculators Menu */}
                <div className="relative group">
                  <Link to="/calculators" className="text-white hover:text-gray-300 px-3 py-2 inline-flex items-center">
                    <Calculator className="w-4 h-4 mr-1" />
                    Calcs <ChevronDown className="ml-1 h-4 w-4" />
                  </Link>
                  <div className="absolute left-0 hidden group-hover:block w-64 py-1 bg-white rounded-md shadow-lg z-50">
                    <Link to="/calculators/interest-rates-compare" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Interest Rates Compare
                    </Link>
                    <Link to="/calculators/treasury-bond-flow-diagram" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Treasury Bond Flow Diagram
                    </Link>
                    <Link to="/calculators/corp-bonds-flow-diagram" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Corp Bonds Flow Diagram
                    </Link>
                    <Link to="/calculators/mbs-flow-diagram" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      MBS Flow Diagram
                    </Link>
                    <Link to="/calculators/equity-flow-diagram" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Equity Flow Diagram
                    </Link>
                    <Link to="/calculators/options-flow-diagram" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Options Flow Diagram
                    </Link>
                    <Link to="/calculators/forwards-flow-diagram" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Forwards Flow Diagram
                    </Link>
                    <Link to="/calculators/futures-flow-diagram" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Futures Flow Diagram
                    </Link>
                    <Link to="/calculators/money-market-funds-flow-diagram" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Money Market Funds Flow Diagram
                    </Link>
                    <Link to="/calculators/mutual-funds-flow-diagram" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Mutual Funds Flow Diagram
                    </Link>
                    <Link to="/calculators/etfs-flow-diagram" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      ETFs Flow Diagram
                    </Link>
                    <Link to="/calculators/budget" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Budget Calculator
                    </Link>
                    <Link to="/calculators/credit-assessment" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Credit Assessment
                    </Link>
                    <Link to="/calculators/emergency-fund" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Emergency Fund Calculator
                    </Link>
                    <Link to="/calculators/renovations" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Renovations Calculator
                    </Link>
                    <Link to="/calculators/retirement" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Retirement Calculator
                    </Link>
                    <Link to="/calculators/true-cost-credit" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      True Cost of Credit
                    </Link>

                  </div>
                </div>

                {/* PnL Menu */}
                <div className="relative group">
                  <Link to="" className="text-white hover:text-gray-300 px-3 py-2 inline-flex items-center">
                    <FileSpreadsheet className="w-4 h-4 mr-1" />
                    PnL  <ChevronDown className="ml-1 h-4 w-4" />
                  </Link>
                  <div className="absolute left-0 hidden group-hover:block w-64 py-1 bg-white rounded-md shadow-lg z-50">
                    <Link to="/pnl/cap-tables" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Cap Tables Dashboard
                    </Link>
                    <Link to="/pnl/pnl-dashboard" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      PnL Dashboard
                    </Link>
                    <Link to="/pnl/family-legacy" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Family Legacy
                    </Link>
                    <Link to="/pnl/investment-mgmt" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Investment Mgmt
                    </Link>
                    <Link to="/pnl/financial-planning" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Financial Planning
                    </Link>
                    <Link to="/pnl/technology-services" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Technology Services
                    </Link>
                    <Link to="/pnl/research-analysis" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Research and Analysis
                    </Link>
                    <Link to="/pnl/portfolio-risk" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Portfolio Risk
                    </Link>
                  </div>
                </div>


                 {/* Training Menu */}
                 <div className="relative group">
                  <Link to="" className="text-white hover:text-gray-300 px-3 py-2 inline-flex items-center">
                    <School className="w-4 h-4 mr-1" />
                    Train <ChevronDown className="ml-1 h-4 w-4" />
                  </Link>
                  <div className="absolute left-0 hidden group-hover:block w-64 py-1 bg-white rounded-md shadow-lg z-50">
                    <Link to="/training/time-value-of-money" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Time Value of Money
                    </Link>
                    <Link to="/training/budget" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Budgeting
                    </Link>
                    <Link to="/training/debt-management" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Debt Management
                    </Link>
                    <Link to="/training/capital-structure" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Capital Structure
                    </Link>
                    <Link to="/training/corporate-valuation" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Corporate Valuation
                    </Link>
                    <Link to="/training/corporate-bond" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Corporate Bond
                    </Link>
                    <Link to="/training/real-estate-fundamentals" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Real Estate - Fundamentals 
                    </Link>
                    <Link to="/training/real-estate-residential" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Real Estate - Residential
                    </Link>
                    <Link to="/training/real-estate-commercial" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Real Estate - Commercial 
                    </Link>
                    <Link to="/training/re-development-construction" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Real Estate - Development & Construction 
                    </Link>
                    <Link to="/training/re-property-management" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Real Estate - Property Management 
                    </Link>
                    <Link to="/training/real-estate-finance" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Real Estate - Finance and Financing
                    </Link>
                    <Link to="/training/treasury-yield-training" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Treasury Yields
                    </Link>
                    <Link to="/training/treasuries-credit" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Treasuries and Credit
                    </Link>
                    <Link to="/training/treasuries-housing" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Treasuries and Housing
                    </Link>
                    <Link to="/training/react-novice" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      React Training - Novice
                    </Link>
                  
                  </div>
                </div>


                 {/* HNW Menu */}
                 <div className="relative group">
                  <Link to="" className="text-white hover:text-gray-300 px-3 py-2 inline-flex items-center">
                    <DollarSign className="w-4 h-4 mr-1" />
                    HNW <ChevronDown className="ml-1 h-4 w-4" />
                  </Link>
                  <div className="absolute left-0 hidden group-hover:block w-64 py-1 bg-white rounded-md shadow-lg z-50">
                    <Link to="/hnw/hnw-intake" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      HNW Intake
                    </Link>
                    <Link to="/hnw/risk-tolerance-assessment" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Risk Tolerance Assessment
                    </Link>
                    <Link to="/hnw/strat-advise" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Strat Advise
                    </Link>
                    
                    <Link to="/hnw/equities-dashboard" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Equities Dashboard
                    </Link>
                    <Link to="/hnw/insider-trading-modeling" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Insider Trading Modeling
                    </Link>
                    <Link to="/hnw/market-movers-momentum-modeling" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Market Movers Momentum Modeling
                    </Link>
                    <Link to="/hnw/sector-analysis-modeling" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Sector Analysis Modeling
                    </Link>
                    <Link to="/hnw/equities-earnings-calls" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Equities Earnings Calls Modeling
                    </Link>
                    
                    <Link to="/hnw/equities-fx-technicals" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Equities/FX Technicals
                    </Link>
                    <Link to="/hnw/options-modeling" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Options Modeling
                    </Link>
                    <Link to="/hnw/fx-modeling" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      FX Modeling
                    </Link>
                    <Link to="/hnw/macros-modeling" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Macros Modeling
                    </Link>
                    <Link to="/hnw/commodities-modeling" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Commodities Modeling
                    </Link>
                    <Link to="/hnw/real-estate-modeler" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Real Estate Modeler
                    </Link>
                    <Link to="/hnw/real-estate-analayis" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Real Estate Analysis
                    </Link>
                    <Link to="/hnw/discourse-tracker" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Discourse Tracker
                    </Link>
                    
                  </div>
                </div>

                {/* AI Menu */}
                <div className="relative group">
                  <Link to="" className="text-white hover:text-gray-300 px-3 py-2 inline-flex items-center">
                    <Brain className="w-4 h-4 mr-1" />
                    AI <ChevronDown className="ml-1 h-4 w-4" />
                  </Link>
                  <div className="absolute left-0 hidden group-hover:block w-64 py-1 bg-white rounded-md shadow-lg z-50">
                    <Link to="/ai/aitwin-business-form" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      AI Twin Business Lead Form
                    </Link>
                    <Link to="/ai/aitwin-community-leadform" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      AI Twin Community 
                    </Link>
                    <Link to="/ai/aitwin-career-transition-leadform" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      AI Twin Career Transition Lead Form
                    </Link>
                    
                    <Link to="/ai/ai-intro-planner" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      AI Twin Intro Planner
                    </Link>
                    <Link to="/ai/ai-hr-planner" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      AI Twin HR Planner
                    </Link>
                    <Link to="/ai/ai-marketing-planner"  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      AI Twin Marketing Planner
                    </Link>
                    

                    <Link to="/ai/ai-agent-business-planner" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      AI Agent Business Planner
                    </Link>
                    <Link to="/ai/ai-protection-planner" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      AI Protection Planner
                    </Link>
                    <Link to="/ai/ai-monetization-planner" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      AI Monetization Planner
                    </Link>
                    <Link to="/ai/ai-roi-estimator" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      AI ROI Estimator
                    </Link>
                    <Link to="/ai/ai-valuation" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      AI Valuation
                    </Link>
                    <Link to="/ai/ai-business-advance" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      AI Business Advance 
                    </Link>

                    <Link to="/ai/agent-test-component" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Agent Test Component
                    </Link>
                    <Link to="/ai/ai-branding-marketing-team" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      AI Branding & Marketing Team
                    </Link>
                    
                    <Link to="/ai/ai-hr-roi-estimator" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      AI HR ROI Estimator
                    </Link>
                    
                    <Link to="/ai/ai-prospecting" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      AI Prospecting
                    </Link>
                    <Link to="/ai/ai-twin-impact-dashboard" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      AI Twin Impact Dashboard
                    </Link>
                    
                  </div>
                </div>

                {/* NP Non-profit Menu */}
                <div className="relative group">
                  <Link to="" className="text-white hover:text-gray-300 px-3 py-2 inline-flex items-center">
                    <HelpingHand className="w-4 h-4 mr-1" />
                    NP <ChevronDown className="ml-1 h-4 w-4" />
                  </Link>
                  <div className="absolute left-0 hidden group-hover:block w-64 py-1 bg-white rounded-md shadow-lg z-50">
                    <Link to="/np/secure-np-planner" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                        Secure Non-Profit Planner
                      </Link>
                      <Link to="/np/np-economics-planner"  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                        Non-Profit Economics Planner
                      </Link>
                    <Link to="/np/nonprofit-security-lead-form" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Secure Non-Profit Lead Form
                    </Link>
                    <Link to="/np/data-product-page" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Secure Non-Profit Data Product Page
                    </Link>
                    <Link to="/np/np-economics-data-product-page" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Non-Profit Economics Data Product Page
                    </Link>

                    <Link to="/np/np-prospecting" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Non-Profit Prospecting
                    </Link>

                    <Link to="/np/impact-products/data-security" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Data Security
                    </Link>
                    <Link to="/np/impact-products/donor-demographics" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Donor Demographics
                    </Link>
                    <Link to="/np/impact-products/economic-cycles" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Economic Cycles
                    </Link>
                    <Link to="/np/impact-products/economics-utils" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Economics Utils
                    </Link>
                    <Link to="/np/impact-products/email-security" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Email Security
                    </Link>
                    <Link to="/np/impact-products/file-security" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      File Security
                    </Link>
                    <Link to="/np/impact-products/financial-security" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Financial Security
                    </Link>
                    <Link to="/np/impact-products/fundraising-strategies" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Fundraising Strategies
                    </Link>
                    <Link to="/np/impact-products/government-funding" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Government Funding
                    </Link>
                    <Link to="/np/impact-products/information-security" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Information Security
                    </Link>
                    <Link to="/np/impact-products/operational-security" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Operational Security
                    </Link>
                    <Link to="/np/impact-products/resilience-playbook" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Resilience Playbook
                    </Link>
                    
                  </div>
                </div>
                  

                  {/* PRV Menu */}
                 <div className="relative group">
                  <Link to="" className="text-white hover:text-gray-300 px-3 py-2 inline-flex items-center">
                    <School2 className="w-4 h-4 mr-1" />
                    PRV <ChevronDown className="ml-1 h-4 w-4" />
                  </Link>
                  <div className="absolute left-0 hidden group-hover:block w-64 py-1 bg-white rounded-md shadow-lg z-50">
                    <Link to="/prv/food-truck-planner" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Food Truck Planner
                    </Link>
                    <Link to="/prv/retirement-calc-planner" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Retirement Calc Planner
                    </Link>
                    <Link to="/prv/credit-assessment-planner" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Credit Assessment Planner
                    </Link>
                    <Link to="/prv/understanding-taxes-planner" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Understanding Taxes Planner
                    </Link>
                    <Link to="/prv/budget-planner" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Budget Planner
                    </Link>
                    <Link to="/prv/prv-prospecting" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      PRV Prospecting
                    </Link>
                    <Link to="/prv/prv-ecosystem-wheel-generator" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      PRV Ecosystem Wheel Generator
                    </Link>
                    
                    <Link to="/prv/food-truck" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Food Truck
                    </Link>
                    <Link to="/prv/food-truck-marketing" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Food Truck - Marketing
                    </Link>
                    <Link to="/prv/food-truck-equipment" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Food Truck - Equipmnent 
                    </Link>
                    <Link to="/prv/food-truck-workers" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Food Truck - Workers  
                    </Link>
                    <Link to="/prv/food-truck-startup-costs" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Food Truck - Startup Costs  
                    </Link>
                    <Link to="/prv/food-truck-insurance" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Food Truck - Insurance 
                    </Link>
                    <Link to="/prv/food-truck-bank-credit-accounts" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Food Truck - Bank & Credit Accounts  
                    </Link>
                    <Link to="/prv/food-truck-financing" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Food Truck - Financing options  
                    </Link>
                    <Link to="/prv/food-truck-revenue-cost-benefit" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Food Truck - Revenue Cost vs. Benefit
                    </Link>
                    
                    
                  </div>
                </div>



                {/* Articles Menu */}
                {renderArticlesDropdown()}

                {/* MMG Menu */}
                {hasRole('MMG') && (
                <div className="relative group">
                  <Link to="" className="text-white hover:text-gray-300 px-3 py-2 inline-flex items-center">
                  <User className="w-4 h-4 mr-1" />
                  MMG <ChevronDown className="ml-1 h-4 w-4" />
                  </Link>
                  <div className="absolute left-0 hidden group-hover:block w-64 py-1 bg-white rounded-md shadow-lg z-50">
                    <Link to="/mmg/mmg-exam" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      MMG Exam
                    </Link>
      
                  </div>
                </div>
                )}
            
              

                 {/* HOA Menu */}
                 <div className="relative group">
                  <Link to="" className="text-white hover:text-gray-300 px-3 py-2 inline-flex items-center">
                  <User className="w-4 h-4 mr-1" />
                    HOA <ChevronDown className="ml-1 h-4 w-4" />
                  </Link>
                  <div className="absolute left-0 hidden group-hover:block w-64 py-1 bg-white rounded-md shadow-lg z-50">
                    <Link to="/hoa/hoa-community-connect" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      HOA Community Connect
                    </Link>
                    <Link to="/hoa/hoa-transform" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      HOA Transform
                    </Link>
                    <Link to="/hoa/hoa-our-home" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      HOA Our Home
                    </Link>
                    <Link to="/hoa/our-home-vendor-mgmt" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Our Home Vendor Mgmt
                    </Link>
                    
                    <Link to="/hoa/hoa-finance-pulse" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      HOA Finance Pulse
                    </Link>
                    <Link to="/hoa/hoa-finance-data-manager" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      HOA Finance Data Manager 
                    </Link>
                    <Link to="/hoa/haris" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      HARIS - HOA Risk Analytics & Intelligence Score
                    </Link>
                    <Link to="/hoa/hoa-asset-shield" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      HOA Asset Shield 
                    </Link>
                    <Link to="/hoa/hoa-membership" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      HOA Membership
                    </Link>
                    <Link to="/hoa/hoa-financials" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      HOA Financials
                    </Link>
                    <Link to="/hoa/hoa-compliance-sync" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      HOA Compliance Sync
                    </Link>
                    <Link to="/hoa/premium-hoa-tuneup" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Premium HOA Tuneup
                    </Link>
                    <Link to="/hoa/premium-hoa-tuneup-data-manager" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Premium HOA Tuneup Data Manager
                    </Link>
                    
                    <Link to="/hoa/premium-hoa-tuneup-assessment" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Premium HOA Tuneup Assessment
                    </Link>
                    <Link to="/hoa/hoa-flow-diagram" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      HOA Flow Diagram
                    </Link>
                    <Link to="/hoa/hoa-tuneup-flow-diagram" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      HOA Tuneup Flow Diagram
                    </Link>
                    <Link to="/hoa/hoa-operating-dashboard" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                       HOA Operating Dashboard
                    </Link>
                    <Link to="/hoa/hoa-roi-estimator" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                       HOA ROI Estimator
                    </Link>
                    <Link to="/generate-referral-code" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                       Generate Referral Code 
                    </Link>
                    <Link to="/hoa/referral-dashboard" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                       Referral Dashboard
                    </Link>
                    
                    <Link to="/cohorts/cohort-creator" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Cohort Creator
                    </Link>
                    <Link to="/cohorts/cohort-selection" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Cohort Selection
                    </Link>

                  </div>
                </div>


                {/* Our Home Menu */}
                <div className="relative group">
                  <Link to="" className="text-white hover:text-gray-300 px-3 py-2 inline-flex items-center">
                  <HomeIcon className="w-4 h-4 mr-1" />
                    OH <ChevronDown className="ml-1 h-4 w-4" />
                  </Link>
                  <div className="absolute left-0 hidden group-hover:block w-64 py-1 bg-white rounded-md shadow-lg z-50">
                  <Link to="/oh/oh-our-home" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      OH Our Home
                    </Link>
                    
                    <Link to="/oh/oh-home-asset-maintain-modeler" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      OH Home Asset Maintain Modeler
                    </Link>
                    <Link to="/oh/oh-our-home-subscribe" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      OH Our Home Subscribe
                    </Link>
                    <Link to="/oh/oh-our-car" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      OH Our Car
                    </Link>
                    <Link to="/oh/oh-our-car-subscribe" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      OH Our Car Subscribe
                    </Link>
                   <Link to="/oh/oh-car-asset-maintain-modeler" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      OH Car Asset Maintain Modeler
                    </Link>
                    <Link to="/oh/car-sales-profitability" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      OH Car Sales Profitabiliity
                    </Link>
                    <Link to="/oh/free-turnover-cost-report" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Free Turnover Cost Report
                    </Link>
                    <Link to="/oh/rooftop-optimize" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      OH Rooftop Optimize
                    </Link>
                    <Link to="/oh/oh-brokerage-dashboard" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      OH Brokerage Dashboard
                    </Link>
                    <Link to="/oh/ohm-user-profile" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Ohm User Profile
                    </Link>
                    <Link to="/oh/ohm-user-profile-re" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Ohm User Profile RE
                    </Link>
                    <Link to="/oh/oh-real-estate-agent-subscribe" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      OH Real Estate Agent Subscribe
                    </Link>
                    
                    <Link to="/oh/oh-home-vendor-subscribe" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      OH Home Vendor Subscribe
                    </Link>
                    <Link to="/oh/oh-our-home-vendor-setup" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      OH Our Home Vendor Setup
                    </Link>
                    <Link to="/oh/oh-our-car-vendor-setup" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      OH Our Car Vendor Setup
                    </Link>

                    <Link to="/hoa/hoa-operating-dashboard" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                       Our Home Operating Dashboard #TODO
                    </Link>
                    <Link to="/hoa/hoa-roi-estimator" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                       Our Home ROI Estimator #TODO
                    </Link>
                    <Link to="/generate-referral-code" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                       Generate Referral Code #TODO
                    </Link>
                    <Link to="/hoa/referral-dashboard" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                       Our Home Referral Dashboard #TODO
                    </Link>
                    
                    <Link to="/cohorts/cohort-creator" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Cohort Creator #TODO
                    </Link>
                    <Link to="/cohorts/cohort-selection" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Cohort Selection #TODO
                    </Link>

                  </div>
                </div>


                {/* RE Menu */}
                <div className="relative group">
                  <Link to="" className="text-white hover:text-gray-300 px-3 py-2 inline-flex items-center">
                  <User className="w-4 h-4 mr-1" />
                    RE <ChevronDown className="ml-1 h-4 w-4" />
                  </Link>
                  <div className="absolute left-0 hidden group-hover:block w-64 py-1 bg-white rounded-md shadow-lg z-50">
                    <Link to="/re/orgstructurevisualization" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Org Structure Visualization
                    </Link>
                    <Link to="/re/property-share" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Property Share
                    </Link>
                    <Link to="/re/re-integration-points" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      RE Integration Points
                    </Link>
                    <Link to="/re/re-platform-security" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      RE Platform Security
                    </Link>
                    <Link to="/re/roi-estimator" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      ROI Estimator
                    </Link>
                    
                    <Link to="/re/offering-builder" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Offering Builder
                    </Link>
                    <Link to="/re/pipeline-intake" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      PipeLine Intake
                    </Link>
                    
                    <Link to="/re/transaction-roles" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Transaction Roles
                    </Link>
                    <Link to="/checklists" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Org Checklists
                    </Link>
                    <Link to="/re/proposal-builder" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Proposal Builder
                    </Link>
                    <Link to="/re/comparables" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Comparables
                    </Link>
                    
                    <Link to="/re/transaction-profitability" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Transaction Profitability
                    </Link>
                    <Link to="/re/vendor-mgmt" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Vendor Manamgement 
                    </Link>
                    <Link to="/re/transactions" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Transactions
                    </Link>
                    <Link to="/re/re-transaction-tracker" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      RE Transaction Tracker
                    </Link>
                    <Link to="/re/re-listings" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      RE Listings
                    </Link>
                    <Link to="/re/re-team" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      RE Team
                    </Link>
                    <Link to="/re/team-feedback" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Team Feedback
                    </Link>
                    <Link to="/re/training-module" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Training Module
                    </Link>
                    <Link to="/re/re-training" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      RE Training
                    </Link>
                    <Link to="/re/re-dashboard" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      RE Dashboard
                    </Link>
                    <Link to="/re/pipeline-dashboard" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Pipeline Dashboard
                    </Link>
                    <Link to="/cohorts/cohort-creator" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Cohort Creator
                    </Link>
                    <Link to="/cohorts/cohort-selection" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Cohort Selection
                    </Link>

                    <Link to="/re/re-onboarding-process" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      RE Onboarding Process
                    </Link>
                    <Link to="/re/re-prospecting" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      RE Prospecting
                    </Link>
                    <Link to="/re/re-agent-performance" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      RE Agent Performance
                    </Link>
                    
                    <Link to="/re/luxury-acquisition-eval" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Luxury Acquisition Eval
                    </Link>
                    <Link to="/tools/faq" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      FAQ
                    </Link>
                    
                    <div className="border-t border-gray-100 mt-1 pt-1">
                      <button
                        onClick={handleLogout}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Sign Out
                      </button>
                    </div>
                  </div>
                </div>

                {/* Account Menu */}
                <div className="relative group">
                  <Link to="/account" className="text-white hover:text-gray-300 px-3 py-2 inline-flex items-center">
                  <User className="w-4 h-4 mr-1" />
                    Account <ChevronDown className="ml-1 h-4 w-4" />
                  </Link>
                  <div className="absolute left-0 hidden group-hover:block w-64 py-1 bg-white rounded-md shadow-lg z-50">
                    <Link to="/account" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Account Settings
                    </Link>

                    <Link to="/subscriptionplans" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Subscription Plans
                    </Link>
                
                    {/*user?.roles?.some(role => role.permissions.includes('admin')) && (*/}

                      <Link to="/admin" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                        Admin Panel
                      </Link>
                      <Link to="/admin-cognito" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                        Admin Cognito
                      </Link>
                      <Link to="/login-cognito" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                        Login Cognito
                      </Link>
                      <Link to="/register-cognito" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                        Register Cognito
                      </Link>
                      <Link to="/zip-locator-widget" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                        Zip Locator Widget
                      </Link>
                      
                      <Link to="/infrastructure-dashboard" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                        Infrastructure Dashboard
                      </Link>
                      <Link to="/admin/product-packages" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                        Product Packages
                      </Link>
                      <Link to="/milestones/milestone-dashboard"className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                        Milestone Dashboard
                      </Link> 
                      <Link to="/role-test" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                        Role Test
                      </Link>
                      <Link to="/prospecting" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                        Prospecting
                      </Link>
                      <Link to="/process-manager" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                        Process Manager
                      </Link>
                      

                      {/* Projects submenu inside Account dropdown */}
                      <div 
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center justify-between cursor-pointer"
                        onClick={(e) => {
                          e.preventDefault();
                          setShowProjectsSubmenu(!showProjectsSubmenu);
                        }}
                      >
                        <span>Projects</span>
                        <ChevronDown className={`h-4 w-4 transition-transform ${showProjectsSubmenu ? 'rotate-180' : ''}`} />
                      </div>

                      {showProjectsSubmenu && (
                        <div className="ml-4 py-1 border-l-2 border-gray-200 pl-2">
                          <Link to="/projects" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                            All Projects
                          </Link>
                          <Link to="/projects/new" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                            New Project
                          </Link>
                        </div>
                      )}

                   {/*} )*/}
                    <div className="border-t border-gray-100 mt-1 pt-1">
                      <button
                        onClick={handleLogout}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Sign Out
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Mobile menu button */}
              <div className="md:hidden flex items-center">
                <button
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="text-gray-400 hover:text-white p-2"
                >
                  {isMobileMenuOpen ? (
                    <X className="h-6 w-6" />
                  ) : (
                    <Menu className="h-6 w-6" />
                  )}
                </button>
              </div>
            </>
          ) : (
            // Non-authenticated navigation
            <div className="hidden md:flex items-center space-x-4">
              <Link to="/login" className="text-white hover:text-gray-300">
                Sign in
              </Link>
              <Link to="/register" className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600">
                Start Free Trial
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Mobile menu */}
      {isMobileMenuOpen && user && (
        <div className="md:hidden bg-gray-800 p-4">
          <div className="space-y-4">
            <div className="text-gray-300 text-3xs">
              {user.email} : {getCurrentPageName()}
            </div>
            
            <div className="flex flex-col space-y-2">

              {/* Portfolio Mobile Menu */}
              <div className="relative">
                <button 
                  className="text-white hover:text-gray-300 w-full text-left flex items-center justify-between" 
                  onClick={() => setActiveMenu(activeMenu === 'portfolio' ? null : 'portfolio')}
                >
                  <span className="flex items-center">
                    <Wallet className="w-4 h-4 mr-2" />
                    Port
                  </span>
                  <ChevronDown className="w-4 h-4" />
                </button>
                <div className={`mt-2 ml-6 space-y-2 ${activeMenu === 'portfolio' ? 'block' : 'hidden'}`}>
                  <Link to="/portfolio/currentholdings" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Current Holdings
                  </Link>
                  <Link to="/portfolio/analysis" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Portfolio Analysis
                  </Link>
                  <Link to="/portfolio/performance" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Portfolio Performance
                  </Link>
                  <Link to="/portfolio/scenarios" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Portfolio Scenarios
                  </Link>
                  <Link to="/portfolio/globalmacrodashboard" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Portfolio Global Macro Dashboard
                  </Link>
                  <Link to="/portfolio/historical-currency-rates" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Historical Currency Rates
                  </Link>
                </div>
              </div>

              {/* Calculators Mobile Menu */}
              <div className="relative">
                <button 
                  className="text-white hover:text-gray-300 w-full text-left flex items-center justify-between" 
                  onClick={() => setActiveMenu(activeMenu === 'calculator' ? null : 'calculator')}
                >
                  <span className="flex items-center">
                    <Calculator className="w-4 h-4 mr-2" />
                    Calcs
                  </span>
                  <ChevronDown className="w-4 h-4" />
                </button>
                <div className={`mt-2 ml-6 space-y-2 ${activeMenu === 'calculator' ? 'block' : 'hidden'}`}>
                  <Link to="/calculators/interest-rates-compare" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Interest Rates Compare
                  </Link>
                  <Link to="/calculators/treasury-bond-flow-diagram" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Treasury Bond Flow Diagram
                  </Link>
                  <Link to="/calculators/corp-bonds-flow-diagram" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Corp Bonds Flow Diagram
                  </Link>
                  <Link to="/calculators/mbs-flow-diagram" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    MBS Flow Diagram
                  </Link>
                  <Link to="/calculators/equity-flow-diagram" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Equity Flow Diagram
                  </Link>
                  <Link to="/calculators/options-flow-diagram" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Options Flow Diagram
                  </Link>
                  <Link to="/calculators/forwards-flow-diagram" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Forwards Flow Diagram
                  </Link>
                  <Link to="/calculators/futures-flow-diagram" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Futures Flow Diagram
                  </Link>
                  <Link to="/calculators/money-market-funds-flow-diagram" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Money Market Funds Flow Diagram
                  </Link>
                  <Link to="/calculators/mutual-funds-flow-diagram" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Mutual Funds Flow Diagram
                  </Link>
                  <Link to="/calculators/etfs-flow-diagram" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    ETFs Flow Diagram
                  </Link>
                  <Link to="/calculators/budget" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Budget Calculator
                  </Link>
                  <Link to="/calculators/credit-assessment" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Credit Assessment
                  </Link>
                  <Link to="/calculators/emergency-fund" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Emergency Fund Calculator
                  </Link>
                  <Link to="/calculators/renovations" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Renovations Calculator
                  </Link>
                  <Link to="/calculators/retirement" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Retirement Calculator
                  </Link>
                  <Link to="/calculators/true-cost-credit" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    True Cost of Credit
                  </Link>
                </div>
              </div>

              {/* PnL Mobile Menu */}
              <div className="relative">
                <button 
                  className="text-white hover:text-gray-300 w-full text-left flex items-center justify-between" 
                  onClick={() => setActiveMenu(activeMenu === 'pnlmodels' ? null : 'pnlmodels')}
                >
                  <span className="flex items-center">
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    PnL 
                  </span>
                  <ChevronDown className="w-4 h-4" />
                </button>
                <div className={`mt-2 ml-6 space-y-2 ${activeMenu === 'pnlmodels' ? 'block' : 'hidden'}`}>
                  <Link to="/pnl/pnl-dashboard" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    PnL Dashboard
                  </Link>
                  <Link to="/pnl/family-legacy" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Family Legacy
                  </Link>
                  <Link to="/pnl/investment-mgmt" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Investment Mgmt
                  </Link>
                  <Link to="/pnl/financial-planning" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Financial Planning
                  </Link>
                  <Link to="/pnl/technology-services" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Technology Services
                  </Link>
                  <Link to="/pnl/research-analysis" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Research and Analysis
                  </Link>
                  <Link to="/pnl/portfolio-risk" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Portfolio Risk
                  </Link>
                </div>
              </div>

              {/* Training Mobile Menu */}
              <div className="relative">
                <button 
                  className="text-white hover:text-gray-300 w-full text-left flex items-center justify-between" 
                  onClick={() => setActiveMenu(activeMenu === 'training' ? null : 'training')}
                >
                  <span className="flex items-center">
                    <School className="w-4 h-4 mr-2" />
                    Train
                  </span>
                  <ChevronDown className="w-4 h-4" />
                </button>
                <div className={`mt-2 ml-6 space-y-2 ${activeMenu === 'training' ? 'block' : 'hidden'}`}>
                  <Link to="/training/time-value-of-money" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Time Value of Money
                  </Link>
                  <Link to="/training/budget" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Budgeting
                  </Link>
                  <Link to="/training/debt-management" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Debt Management
                  </Link>
                  <Link to="/training/capital-structure" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Capital Structure
                  </Link>
                  <Link to="/training/corporate-valuation" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Corporate Valuation
                  </Link>
                  <Link to="/training/corporate-bond" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Corporate Bond
                  </Link>
                  <Link to="/training/real-estate-fundamentals" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Real Estate - Fundamentals 
                  </Link>
                  <Link to="/training/real-estate-residential" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Real Estate - Residential
                  </Link>
                  <Link to="/training/real-estate-commercial" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Real Estate - Commercial 
                  </Link>
                  <Link to="/training/re-development-construction" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Real Estate - Development & Construction 
                  </Link>
                  <Link to="/training/re-property-management" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Real Estate - Property Management 
                  </Link>
                  <Link to="/training/real-estate-finance" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Real Estate - Finance and Financing
                  </Link>
                  <Link to="/training/treasury-yield-training" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Treasury Yields
                  </Link>
                  <Link to="/training/treasuries-credit" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Treasuries and Credit
                  </Link>
                  <Link to="/training/treasuries-housing" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Treasuries and Housing
                  </Link>
                  <Link to="/training/react-novice" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    React Training - Novice
                  </Link>
                </div>
              </div>

               {/* HNW Mobile Menu */}
              <div className="relative">
                <button 
                  className="text-white hover:text-gray-300 w-full text-left flex items-center justify-between" 
                  onClick={() => setActiveMenu(activeMenu === 'hnw' ? null : 'hnw')}
                >
                  <span className="flex items-center">
                    <DollarSign className="w-4 h-4 mr-2" />
                    HNW
                  </span>
                  <ChevronDown className="w-4 h-4" />
                </button>
                <div className={`mt-2 ml-6 space-y-2 ${activeMenu === 'hnw' ? 'block' : 'hidden'}`}>
                  <Link to="/hnw/hnw-intake" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    HNW Intake
                  </Link>
                  <Link to="/hnw/risk-tolerance-assessment" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Risk Tolerance Assessment
                  </Link>
                  <Link to="/hnw/strat-advise" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Strat Advise
                  </Link>
                  <Link to="/hnw/equities-dashboard" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Equities Dashboard
                  </Link>
                  <Link to="/hnw/insider-trading-modeling" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Insider Trading Modeling
                  </Link>
                  <Link to="/hnw/market-movers-momentum-modeling" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Market Movers Momentum Modeling
                  </Link>
                  <Link to="/hnw/sector-analysis-modeling" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Sector Analysis Modeling
                  </Link>
                  <Link to="/hnw/equities-earnings-calls" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Equities Earnings Calls Modeling
                  </Link>
                  <Link to="/hnw/equities-fx-technicals" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Equities/FX Technicals
                  </Link>
                  <Link to="/hnw/options-modeling" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Options Modeling
                  </Link>
                  <Link to="/hnw/fx-modeling" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    FX Modeling
                  </Link>
                  <Link to="/hnw/macros-modeling" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Macros Modeling
                  </Link>
                  <Link to="/hnw/commodities-modeling" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Commodities Modeling
                  </Link>
                  <Link to="/hnw/real-estate-modeler" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Real Estate Modeler
                  </Link>
                  <Link to="/hnw/real-estate-analayis" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Real Estate Analysis
                  </Link>
                  <Link to="/hnw/discourse-tracker" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Discourse Tracker
                  </Link>
                </div>
              </div>

               {/* AI Mobile Menu */}
              <div className="relative">
                <button 
                  className="text-white hover:text-gray-300 w-full text-left flex items-center justify-between" 
                  onClick={() => setActiveMenu(activeMenu === 'ai' ? null : 'ai')}
                >
                  <span className="flex items-center">
                    <Brain className="w-4 h-4 mr-2" />
                    AI
                  </span>
                  <ChevronDown className="w-4 h-4" />
                </button>
                <div className={`mt-2 ml-6 space-y-2 ${activeMenu === 'ai' ? 'block' : 'hidden'}`}>
                  <Link to="/ai/aitwin-business-form" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    AI Twin Business Lead Form
                  </Link>
                  <Link to="/ai/aitwin-community-leadform" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    AI Twin Community
                  </Link>
                  <Link to="/ai/aitwin-career-transition-leadform" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    AI Twin Career Transition Lead Form
                  </Link>
                  <Link to="/ai/ai-intro-planner" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    AI Twin Intro Planner
                  </Link>
                  <Link to="/ai/ai-hr-planner" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    AI Twin HR Planner
                  </Link>
                  <Link to="/ai/ai-agent-business-planner" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    AI Agent Business Planner
                  </Link>
                  <Link to="/ai/ai-protection-planner" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    AI Protection Planner
                  </Link>
                  <Link to="/ai/ai-monetization-planner" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    AI Monetization Planner
                  </Link>
                  <Link to="/ai/ai-marketing-planner" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    AI Twin Marketing Planner
                  </Link>
                  <Link to="/ai/ai-roi-estimator" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    AI ROI Estimator
                  </Link>
                  <Link to="/ai/ai-hr-roi-estimator" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    AI HR ROI Estimator
                  </Link>
                  <Link to="/ai/ai-valuation" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    AI Valuation
                  </Link>
                  <Link to="/ai/ai-prospecting" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    AI Prospecting
                  </Link>
                  <Link to="/ai/ai-twin-impact-dashboard" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    AI Twin Impact Dashboard
                  </Link>
                </div>
              </div>

              {/* NP Non-profit Mobile Menu */}
              <div className="relative">
                <button 
                  className="text-white hover:text-gray-300 w-full text-left flex items-center justify-between" 
                  onClick={() => setActiveMenu(activeMenu === 'np' ? null : 'np')}
                >
                  <span className="flex items-center">
                    <HelpingHand className="w-4 h-4 mr-2" />
                    NP
                  </span>
                  <ChevronDown className="w-4 h-4" />
                </button>
                <div className={`mt-2 ml-6 space-y-2 ${activeMenu === 'np' ? 'block' : 'hidden'}`}>
                  <Link to="/np/secure-np-planner" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Secure Non-Profit Planner
                  </Link>
                  <Link to="/np/np-economics-planner" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Non-Profit Economics Planner
                  </Link>
                  <Link to="/np/nonprofit-security-lead-form" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Secure Non-Profit Lead Form
                  </Link>
                  <Link to="/np/data-product-page" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Secure Non-Profit Data Product Page
                  </Link>
                  <Link to="/np/np-economics-data-product-page" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Non-Profit Economics Data Product Page
                  </Link>
                  <Link to="/np/np-prospecting" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Non-Profit Prospecting
                  </Link>
                </div>
              </div>

              {/* PRV Mobile Menu */}
              <div className="relative">
                <button 
                  className="text-white hover:text-gray-300 w-full text-left flex items-center justify-between" 
                  onClick={() => setActiveMenu(activeMenu === 'prv' ? null : 'prv')}
                >
                  <span className="flex items-center">
                    <School2 className="w-4 h-4 mr-2" />
                    PRV
                  </span>
                  <ChevronDown className="w-4 h-4" />
                </button>
                <div className={`mt-2 ml-6 space-y-2 ${activeMenu === 'prv' ? 'block' : 'hidden'}`}>
                  <Link to="/prv/food-truck-planner" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Food Truck Planner
                  </Link>
                  <Link to="/prv/retirement-calc-planner" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Retirement Calc Planner
                  </Link>
                  <Link to="/prv/credit-assessment-planner" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Credit Assessment Planner
                  </Link>
                  <Link to="/prv/understanding-taxes-planner" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Understanding Taxes Planner
                  </Link>
                  <Link to="/prv/budget-planner" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Budget Planner
                  </Link>
                  <Link to="/prv/prv-prospecting" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    PRV Prospecting
                  </Link>
                  <Link to="/prv/prv-ecosystem-wheel-generator" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    PRV Ecosystem Wheel Generator
                  </Link>
                  <Link to="/prv/food-truck" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Food Truck
                  </Link>
                  <Link to="/prv/food-truck-marketing" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Food Truck - Marketing
                  </Link>
                  <Link to="/prv/food-truck-equipment" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Food Truck - Equipment
                  </Link>
                  <Link to="/prv/food-truck-workers" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Food Truck - Workers
                  </Link>
                  <Link to="/prv/food-truck-startup-costs" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Food Truck - Startup Costs
                  </Link>
                  <Link to="/prv/food-truck-insurance" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Food Truck - Insurance
                  </Link>
                  <Link to="/prv/food-truck-bank-credit-accounts" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Food Truck - Bank & Credit Accounts
                  </Link>
                  <Link to="/prv/food-truck-financing" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Food Truck - Financing Options
                  </Link>
                  <Link to="/prv/food-truck-revenue-cost-benefit" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Food Truck - Revenue Cost vs. Benefit
                  </Link>
                </div>
              </div>
               
              {renderMobileArticlesMenu()}
              
              {/* MMG Mobile Menu */}
              {hasRole('MMG') && (
              <div className="relative">
                <button 
                  className="text-white hover:text-gray-300 w-full text-left flex items-center justify-between" 
                  onClick={() => setActiveMenu(activeMenu === 'mmg' ? null : 'mmg')}
                >
                  <span className="flex items-center">
                    <User className="w-4 h-4 mr-2" />
                    MMG
                  </span>
                  <ChevronDown className="w-4 h-4" />
                </button>
                <div className={`mt-2 ml-6 space-y-2 ${activeMenu === 'mmg' ? 'block' : 'hidden'}`}>
                  <Link to="/mmg/mmg-exam" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    MMG Exam
                  </Link>
                </div>
              </div>
              )}
              
              {/* HOA Mobile Menu */}
              <div className="relative">
                <button 
                  className="text-white hover:text-gray-300 w-full text-left flex items-center justify-between" 
                  onClick={() => setActiveMenu(activeMenu === 'hoa' ? null : 'hoa')}
                >
                  <span className="flex items-center">
                    <User className="w-4 h-4 mr-2" />
                    HOA
                  </span>
                  <ChevronDown className="w-4 h-4" />
                </button>
                <div className={`mt-2 ml-6 space-y-2 ${activeMenu === 'hoa' ? 'block' : 'hidden'}`}>
                  <Link to="/hoa/hoa-community-connect" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    HOA Community Connect
                  </Link>
                  <Link to="/hoa/hoa-finance-pulse" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    HOA Finance Pulse
                  </Link>
                  <Link to="/hoa/hoa-finance-data-manager"  className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    HOA Finance Data Manager
                  </Link>
                  <Link to="/hoa/hoa-financials" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    HOA Financials
                  </Link>
                  <Link to="/hoa/hoa-compliance-sync" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    HOA Compliance Sync
                  </Link>
                  <Link to="/hoa/premium-hoa-tuneup" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Premium HOA Tuneup
                  </Link>
                  <Link to="/hoa/hoa-flow-diagram" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    HOA Flow Diagram
                  </Link>
                  <Link to="/hoa/hoa-tuneup-flow-diagram" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    HOA Tuneup Flow Diagram
                  </Link>
                  <Link to="/hoa/hoa-operating-dashboard" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    HOA Operating Dashboard
                  </Link>
                  <Link to="/hoa/hoa-roi-estimator" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    HOA ROI Estimator
                  </Link>
                  <Link to="/generate-referral-code" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Generate Referral Code
                  </Link>
                  <Link to="/hoa/referral-dashboard" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Referral Dashboard
                  </Link>
                  <Link to="/cohorts/cohort-creator" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Cohort Creator
                  </Link>
                  <Link to="/cohorts/cohort-selection" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Cohort Selection
                  </Link>
                </div>
              </div>

              {/* RE Mobile Menu */}
              <div className="relative">
                <button 
                  className="text-white hover:text-gray-300 w-full text-left flex items-center justify-between" 
                  onClick={() => setActiveMenu(activeMenu === 'phgre' ? null : 'phgre')}
                >
                  <span className="flex items-center">
                    <User className="w-4 h-4 mr-2" />
                    RE
                  </span>
                  <ChevronDown className="w-4 h-4" />
                </button>
                <div className={`mt-2 ml-6 space-y-2 ${activeMenu === 'phgre' ? 'block' : 'hidden'}`}>
                  <Link to="/re/orgstructurevisualization" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Org Structure Visualization
                  </Link>
                  <Link to="/re/property-share" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Property Share
                  </Link>
                  <Link to="/re/re-integration-points" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    RE Integration Points
                  </Link>
                  <Link to="/re/re-platform-security" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    RE Platform Security
                  </Link>
                  <Link to="/re/roi-estimator" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    ROI Estimator
                  </Link>
                  <Link to="/re/offering-builder" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Offering Builder
                  </Link>
                  <Link to="/re/pipeline-intake" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    PipeLine Intake
                  </Link>
                  <Link to="/re/transaction-roles" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Transaction Roles
                  </Link>
                  <Link to="/checklists" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Org Checklists
                  </Link>
                  <Link to="/re/proposal-builder" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Proposal Builder
                  </Link>
                  <Link to="/re/comparables" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Comparables
                  </Link>
                  <Link to="/re/transaction-profitability" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Transaction Profitability
                  </Link>
                  <Link to="/re/vendor-mgmt" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Vendor Management
                  </Link>
                  <Link to="/re/transactions" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Transactions
                  </Link>
                  <Link to="/re/re-transaction-tracker" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    RE Transaction Tracker
                  </Link>
                  <Link to="/re/re-listings" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    RE Listings
                  </Link>
                  <Link to="/re/re-team" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    RE Team
                  </Link>
                  <Link to="/re/team-feedback" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Team Feedback
                  </Link>
                  <Link to="/re/training-module" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Training Module
                  </Link>
                  <Link to="/re/re-training" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    RE Training
                  </Link>
                  <Link to="/re/re-dashboard" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    RE Dashboard
                  </Link>
                  <Link to="/re/pipeline-dashboard" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Pipeline Dashboard
                  </Link>
                  <Link to="/cohorts/cohort-creator" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Cohort Creator
                  </Link>
                  <Link to="/cohorts/cohort-selection" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Cohort Selection
                  </Link>
                  <Link to="/re/re-onboarding-process" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    RE Onboarding Process
                  </Link>
                  <Link to="/re/re-prospecting" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    RE Prospecting
                  </Link>
                  <Link to="/re/re-agent-performance" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    RE Agent Performance
                  </Link>
                  <Link to="/re/luxury-acquisition-eval" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Luxury Acquisition Eval
                  </Link>
                  <Link to="/tools/faq" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    FAQ
                  </Link>
                </div>
              </div>

              {/* Account Mobile Menu */}
              <div className="relative">
                <button 
                  className="text-white hover:text-gray-300 w-full text-left flex items-center justify-between" 
                  onClick={() => setActiveMenu(activeMenu === 'account' ? null : 'account')}
                >
                  <span className="flex items-center">
                    <User className="w-4 h-4 mr-2" />
                    Account
                  </span>
                  <ChevronDown className="w-4 h-4" />
                </button>
                <div className={`mt-2 ml-6 space-y-2 ${activeMenu === 'account' ? 'block' : 'hidden'}`}>
                  <Link to="/account" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Account Settings
                  </Link>
                  <Link to="/subscriptionplans" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Subscription Plans
                  </Link>
                  <Link to="/admin" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Admin Panel
                  </Link>
                  <Link to="/admin-cognito" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Admin Cognito
                  </Link>
                  <Link to="/login-cognito" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Login Cognito
                  </Link>
                  <Link to="/register-cognito" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Register Cognito
                  </Link>
                  <Link to="/infrastructure-dashboard" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Infrastructure Dashboard
                  </Link>
                  <Link to="/admin/product-packages" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Product Packages
                  </Link>
                  <Link to="/role-test" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Role Test
                  </Link>
                  <Link to="/prospecting" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Prospecting
                  </Link>
                  <Link to="/process-manager" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                    Process Manager
                  </Link>
                  
                  {/* Projects submenu inside Account mobile menu */}
                  <button 
                    className="text-white hover:bg-gray-700 w-full text-left flex items-center justify-between px-3 py-2 rounded pl-6 text-sm" 
                    onClick={() => setShowProjectsSubmenu(!showProjectsSubmenu)}
                  >
                    <span>Projects</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${showProjectsSubmenu ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {showProjectsSubmenu && (
                    <div className="mt-1 ml-4 pl-2 space-y-2 border-l border-gray-600">
                      <Link to="/projects" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                        All Projects
                      </Link>
                      <Link to="/projects/new" className="block px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm">
                        New Project
                      </Link>
                    </div>
                  )}
                  
                  <button
                    onClick={handleLogout}
                    className="block w-full text-left px-3 py-2 text-white hover:bg-gray-700 rounded pl-6 text-sm"
                  >
                    Sign Out
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}

export default Navigation;